import { lazy, Suspense, useEffect, useState } from 'react'
import { THEME_DEFINITIONS, themeDisplayName } from './data'
import { useI18n } from './i18n/context'
import { useAuth } from './auth/AuthContext'
import { isAdminUser } from './auth/authorization'
import { getAccountDisplayName } from './app/services/identityService'
import { AlbumSelector } from './components/AlbumSelector'
import {
  ExternalLinkConfirmModal,
  HelpModal,
  LegalAcceptanceModal
} from './app/components/AppModals'
import { AppShell } from './app/components/AppShell'
import { AlbumWorkspaceView } from './app/components/AlbumWorkspaceView'
import { SettingsMenu } from './app/components/SettingsMenu'
import { WorkspaceTabs } from './app/components/WorkspaceTabs'
import { resolveAlbumPageSize } from './app/state'
import { useAppPreferences } from './app/useAppPreferences'
import { useExternalLinkPrompt } from './app/useExternalLinkPrompt'
import { useAlbumWorkspace } from './app/useAlbumWorkspace'
import {
  dispatchOpenHomeEvent,
  useInternalNavigationEvents,
  useSettingsMenu
} from './app/useInternalNavigation'
import { usePersistedAppState } from './app/usePersistedAppState'

const APP_ACCENT = '#f97316'
const APP_ACCENT_SOFT = '#ffedd5'
const APP_ACCENT_SOFT_DARK = '#4a2810'
const ThemeReviewPage = lazy(() =>
  import('./components/ThemeReviewPage').then((module) => ({ default: module.ThemeReviewPage }))
)

function App(): JSX.Element {
  const { t, locale, setLocale } = useI18n()
  const { status: authStatus, user, verifyAdminAccess, signOut } = useAuth()
  const isAdmin = authStatus === 'signedIn' && isAdminUser(user)
  const {
    isLegalAccepted,
    isDarkMode,
    setIsDarkMode,
    albumPageSize,
    setAlbumPageSize,
    acceptLegalTerms
  } = useAppPreferences()
  const {
    pendingExternalUrl,
    setPendingExternalUrl,
    requestOpenExternalLink,
    confirmOpenExternalLink
  } = useExternalLinkPrompt()

  const { appState, setAppState, isHydrated, saveError, setSaveError, retrySave } =
    usePersistedAppState(t.saveError)

  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isThemeReviewOpen, setIsThemeReviewOpen] = useState(false)
  const { isSettingsOpen, setIsSettingsOpen, settingsMenuRef } = useSettingsMenu()

  useEffect(() => {
    if (!isAdmin) setIsThemeReviewOpen(false)
  }, [isAdmin])

  const {
    activeRecord,
    availableAlbumPageSizeOptions,
    stickerNumbers,
    stickerView,
    setStickerView,
    middleFrameTab,
    setMiddleFrameTab,
    selectedSticker,
    setSelectedSticker,
    selectedDestinationId,
    setSelectedDestinationId,
    comments,
    setComments,
    isTargetVisible,
    setIsTargetVisible,
    justCollectedSticker,
    setJustCollectedSticker,
    handleSelectAlbum,
    handleCreateAlbum,
    handleDeleteAlbum,
    handleBackToSelector,
    updateAlbum,
    addDestination,
    removeDestination,
    closeModal,
    saveStickerInvestment,
    removeStickerInvestment
  } = useAlbumWorkspace({
    appState,
    setAppState,
    albumPageSize,
    setAlbumPageSize
  })

  useInternalNavigationEvents({
    onBackToSelector: handleBackToSelector,
    onOpenHistory: () => setMiddleFrameTab('history'),
    onOpenHelp: () => setIsHelpOpen(true),
    onOpenThemeReview: () => {
      void openThemeReviewIfAuthorized()
    }
  })

  async function openThemeReviewIfAuthorized(): Promise<void> {
    if (!isAdmin || !(await verifyAdminAccess())) return
    setIsThemeReviewOpen(true)
  }

  const canOpenAlbumViews = appState.activeAlbumId !== null
  const accountName = getAccountDisplayName(user?.email, t.guestUser)
  const settingsMenu = (
    <SettingsMenu
      t={t}
      locale={locale}
      setLocale={setLocale}
      accountName={accountName}
      authStatus={authStatus}
      isDarkMode={isDarkMode}
      setIsDarkMode={setIsDarkMode}
      canOpenAlbumViews={canOpenAlbumViews}
      setMiddleFrameTab={setMiddleFrameTab}
      handleBackToSelector={handleBackToSelector}
      canOpenThemeReview={isAdmin}
      onOpenThemeReview={() => {
        void openThemeReviewIfAuthorized()
      }}
      setIsHelpOpen={setIsHelpOpen}
      setIsSettingsOpen={setIsSettingsOpen}
      onSignOut={() => void signOut()}
      onOpenHome={dispatchOpenHomeEvent}
      settingsMenuRef={settingsMenuRef}
      isSettingsOpen={isSettingsOpen}
    />
  )

  // ─── Loading screen ────────────────────────────────────────────────────────

  if (!isHydrated) {
    return (
      <AppShell
        accent={APP_ACCENT}
        accentSoft={isDarkMode ? APP_ACCENT_SOFT_DARK : APP_ACCENT_SOFT}
        settingsMenu={settingsMenu}
      >
        <section className="hero-panel loading-panel">
          <div className="loading-content">
            <div className="loading-spinner" role="status" aria-label={t.loading} />
            <p className="eyebrow">{t.appName}</p>
            <h1>{t.loading}</h1>
            <p className="hero-copy">{t.loadingDesc}</p>
          </div>
        </section>
      </AppShell>
    )
  }

  if (isThemeReviewOpen && isAdmin) {
    return (
      <AppShell
        accent={APP_ACCENT}
        accentSoft={isDarkMode ? APP_ACCENT_SOFT_DARK : APP_ACCENT_SOFT}
        settingsMenu={settingsMenu}
      >
        <Suspense fallback={<section className="theme-review-status">{t.loading}</section>}>
          <ThemeReviewPage
            locale={locale}
            onBack={() => setIsThemeReviewOpen(false)}
            onOpenExternalLink={requestOpenExternalLink}
          />
        </Suspense>
        {pendingExternalUrl && (
          <ExternalLinkConfirmModal
            closeLabel={t.cancel}
            onCancel={() => setPendingExternalUrl(null)}
            onConfirm={() => void confirmOpenExternalLink()}
          />
        )}
      </AppShell>
    )
  }

  // ─── Album selector ────────────────────────────────────────────────────────

  if (!activeRecord) {
    return (
      <>
        {settingsMenu}
        <AlbumSelector
          state={appState}
          onSelectAlbum={handleSelectAlbum}
          onCreateAlbum={handleCreateAlbum}
          onDeleteAlbum={handleDeleteAlbum}
          isDarkMode={isDarkMode}
          onToggleTheme={() => setIsDarkMode((value) => !value)}
        />
        {!isLegalAccepted && <LegalAcceptanceModal onAccept={acceptLegalTerms} />}
        {isHelpOpen && (
          <HelpModal
            eyebrow={t.quickLinkHelp}
            title={t.helpTitle}
            body={t.helpDescription}
            closeLabel={t.cancel}
            onClose={() => setIsHelpOpen(false)}
          />
        )}
      </>
    )
  }

  // ─── Main album view ───────────────────────────────────────────────────────

  const theme = THEME_DEFINITIONS[activeRecord.config.theme]
  const themeName = themeDisplayName(theme, t)
  const themeHero = theme.hero

  const middleFrameTabs = (
    <WorkspaceTabs
      middleFrameTab={middleFrameTab}
      setMiddleFrameTab={setMiddleFrameTab}
      ariaLabel={t.myInvestmentAlbum}
      albumLabel={t.myInvestmentAlbum}
      historyLabel={t.tabHistory}
      configurationLabel={t.tabConfiguration}
    />
  )

  return (
    <AppShell
      accent={APP_ACCENT}
      accentSoft={isDarkMode ? APP_ACCENT_SOFT_DARK : APP_ACCENT_SOFT}
      settingsMenu={settingsMenu}
      saveError={saveError}
      saveRetryLabel={t.saveRetry}
      onRetrySave={retrySave}
      onDismissSaveError={() => setSaveError(null)}
    >
      <AlbumWorkspaceView
        activeRecord={activeRecord}
        albumPageSize={albumPageSize}
        availableAlbumPageSizeOptions={availableAlbumPageSizeOptions}
        stickerNumbers={stickerNumbers}
        stickerView={stickerView}
        middleFrameTab={middleFrameTab}
        selectedSticker={selectedSticker}
        selectedDestinationId={selectedDestinationId}
        comments={comments}
        isTargetVisible={isTargetVisible}
        justCollectedSticker={justCollectedSticker}
        theme={theme}
        themeName={themeName}
        themeHero={themeHero}
        middleFrameTabs={middleFrameTabs}
        onSetStickerView={setStickerView}
        onSetAlbumPageSize={(pageSize) =>
          setAlbumPageSize(resolveAlbumPageSize(pageSize, activeRecord.config.size))
        }
        onSelectSticker={setSelectedSticker}
        onBackToSelector={handleBackToSelector}
        onToggleTarget={() => setIsTargetVisible((v) => !v)}
        onUpdateAlbum={updateAlbum}
        onAddDestination={addDestination}
        onRemoveDestination={removeDestination}
        onCloseModal={closeModal}
        onSaveStickerInvestment={saveStickerInvestment}
        onResetStickerInvestment={removeStickerInvestment}
        onCommentsChange={setComments}
        onDestinationChange={setSelectedDestinationId}
        onOpenExternalLink={requestOpenExternalLink}
        onCloseCollectedOverlay={() => setJustCollectedSticker(null)}
        onGoToConfig={() => {
          closeModal()
          setMiddleFrameTab('configuration')
        }}
      />

      {!isLegalAccepted && <LegalAcceptanceModal onAccept={acceptLegalTerms} />}

      {pendingExternalUrl && (
        <ExternalLinkConfirmModal
          closeLabel={t.cancel}
          onCancel={() => setPendingExternalUrl(null)}
          onConfirm={() => void confirmOpenExternalLink()}
        />
      )}
      {isHelpOpen && (
        <HelpModal
          eyebrow={t.quickLinkHelp}
          title={t.helpTitle}
          body={t.helpDescription}
          bullets={[t.clickToRegister, t.noInvestmentsDesc, t.preconfigureDestinations]}
          closeLabel={t.cancel}
          onClose={() => setIsHelpOpen(false)}
        />
      )}
    </AppShell>
  )
}

export default App
